import { Box, Button, CircularProgress, Stack, TextField, useTheme } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { FormField } from '../../../../../form/FormField';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { z } from 'zod';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDrawerContext } from '../../../../../drawer/DrawerContext';
import { useTypedForm } from '../../../../../../../shared/fe/hooks/useForm';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useSnackbar } from '../../../../../store/snackbar';

const subActivityQuery = graphql(`
	query journeyDetail_getActivitySubtouchpoints($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			subTouchpoints {
				id
				type
				activity {
					name
				}
			}
		}
	}
`);

const deleteSubactivityMutation = graphql(`
	mutation journeyDetail_deleteSubTouchpoint($subTouchpointId: UUID!) {
		deleteTouchpoint(by: { id: $subTouchpointId }) {
			ok
			errorMessage
		}
	}
`);

const createSubactivityMutation = graphql(`
	mutation journeyDetail_createSubactivity($parentActivityId: UUID!, $name: String!) {
		createActivity(
			data: { name: $name, touchpoint: { create: { order: 0, type: subactivity, parentActivity: { connect: { id: $parentActivityId } } } } }
		) {
			ok
			errorMessage
		}
	}
`);

export const subactivityEditDrawerSchema = z.object({
	activityId: z.string(),
});

const createFormSchema = () =>
	z.object({
		subactivityName: z.string(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

export const SubactivitiesEditDrawer: FC<z.infer<typeof subactivityEditDrawerSchema>> = ({ activityId }) => {
	const theme = useTheme();
	const intl = useIntl();
	const [initialLoad, setInitialLoad] = useState(true);
	const { closeDrawer } = useDrawerContext();
	const { openSnackbar } = useSnackbar();

	const { data: subactivity, isLoading } = useApiQuery(subActivityQuery, { variables: { activityId } });
	const { mutate: createSubactivity } = useApiMutation(createSubactivityMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.createActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.subactivity.create.success' }),
					error: intl.formatMessage({ id: 'widget.subactivity.create.error' }),
				},
				errorMessage: result.createActivity.errorMessage,
				callback: () => closeDrawer(),
				openSnackbar,
			}),
	});

	const { mutate: deleteSubactivity } = useApiMutation(deleteSubactivityMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.deleteTouchpoint.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.subactivity.delete.success' }),
					error: intl.formatMessage({ id: 'widget.subactivity.delete.error' }),
				},
				errorMessage: result.deleteTouchpoint.errorMessage,
				openSnackbar,
			}),
	});

	const form = useTypedForm({
		schema: createFormSchema(),
		mode: 'all',
		reValidateMode: 'onChange',
	});

	useEffect(() => {
		if (typeof subactivity?.getActivity?.subTouchpoints[0]?.activity?.name === 'string' && initialLoad) {
			form.reset({
				subactivityName: subactivity.getActivity.subTouchpoints[0].activity.name,
			});
			setInitialLoad(false);
		}
	}, [initialLoad, form, subactivity?.getActivity?.subTouchpoints]);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	const { isValid } = form.formState;

	// NOTE: This code will be removed after we enable more subactivities per activity
	const isSubactivityExist = subactivity?.getActivity && subactivity.getActivity.subTouchpoints.length >= 1;
	const onSubmit = (data: FormValues) => {
		const { subactivityName } = data;
		if (isValid) {
			createSubactivity({ parentActivityId: activityId, name: subactivityName });
		}
	};

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.subactivities.title' })} />
			<Box width={'100%'}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<FormField
						form={form}
						name={'subactivityName'}
						label={intl.formatMessage({ id: 'drawer.edit-widget.label.subactivities' })}
						render={(props) => <TextField {...props} />}
					/>
					<Stack direction={'row'} justifyContent={'flex-end'} mt={2}>
						{isSubactivityExist && (
							<Button
								onClick={() =>
									subactivity.getActivity?.subTouchpoints != null &&
									deleteSubactivity({
										subTouchpointId: subactivity.getActivity.subTouchpoints[0].id,
									})
								}
								variant={'contained'}
								sx={{
									backgroundColor: theme.palette.error.main,
									':hover': { backgroundColor: theme.palette.error.dark },
								}}
							>
								<FormattedMessage id={'journey.detail.widget.subactivities.delete'} />
							</Button>
						)}
						{!isSubactivityExist && (
							<Button type={'submit'} variant={'contained'}>
								<FormattedMessage id={'journey.detail.widget.subactivities.submit'} />
							</Button>
						)}
					</Stack>
				</form>
			</Box>
		</DrawerWrapper>
	);
};
